import React, { useState, useEffect, useRef } from "react";
import Checkbox from "../../components/CheckBox";
import CustomDate from "../../components/CustomAccordian/CustomDateWithTime";
import CustomInputBox from "../../components/Input";
import { IoCalendarOutline } from "react-icons/io5";
import { formatDateWithTime } from "../../utils/helper";

interface VariableSelection {
  chargeType: string;
  chargeName: string;
  isSelected: boolean;
  appointmentDate?: string;
  appointmentTimestamp?: number;
  floors?: number;
}

interface OtherDetailsProps {
  onPayloadChange?: (payload: VariableSelection[]) => void;
}

const STORAGE_KEY = "order-creation-other-details";

interface OtherDetailsState {
  appointmentBasedDelivery: boolean;
  selectedDate: string | null; // Store as ISO string
  multiStoreyWithFloor: boolean;
  numberOfFloors: string;
  pod: boolean;
  toPay: boolean;
}

const OtherDetails: React.FC<OtherDetailsProps> = ({ onPayloadChange }) => {
  // Load initial state from localStorage
  const loadInitialState = (): OtherDetailsState => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.error("Error parsing saved other details:", error);
      }
    }
    return {
      appointmentBasedDelivery: false,
      selectedDate: new Date().toISOString(),
      multiStoreyWithFloor: false,
      numberOfFloors: "5",
      pod: false,
      toPay: false,
    };
  };

  const initialState = loadInitialState();

  const [appointmentBasedDelivery, setAppointmentBasedDelivery] = useState(
    initialState.appointmentBasedDelivery
  );

  // Close date picker when checkbox is unchecked
  useEffect(() => {
    if (!appointmentBasedDelivery) {
      setShowDatePicker(false);
    }
  }, [appointmentBasedDelivery]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    initialState.selectedDate ? new Date(initialState.selectedDate) : new Date()
  );
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);

  const [multiStoreyWithFloor, setMultiStoreyWithFloor] = useState(
    initialState.multiStoreyWithFloor
  );
  const [numberOfFloors, setNumberOfFloors] = useState(
    initialState.numberOfFloors
  );

  const [pod, setPod] = useState(initialState.pod);
  const [toPay, setToPay] = useState(initialState.toPay);

  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
    // setShowDatePicker(false);
  };

  const handleFloorsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNumberOfFloors(e.target.value);
  };

  // Generate payload for variable selections
  const generatePayload = (): VariableSelection[] => {
    const payload: VariableSelection[] = [];

    // Appointment Based Delivery
    if (appointmentBasedDelivery && selectedDate) {
      payload.push({
        chargeType: "appointment",
        chargeName: "Appointment-Based Delivery Charges",
        isSelected: true,
        appointmentDate: selectedDate.toISOString(),
        appointmentTimestamp: selectedDate.getTime(),
      });
    }

    // Multi-Storey With Floor
    if (multiStoreyWithFloor && numberOfFloors) {
      payload.push({
        chargeType: "multistory",
        chargeName: "Multi-Story Charges",
        isSelected: true,
        floors: parseInt(numberOfFloors) || 0,
      });
    }

    // POD
    if (pod) {
      payload.push({
        chargeType: "pod",
        chargeName: "POD Charge (Hard Copies)",
        isSelected: true,
      });
    }

    // To Pay
    if (toPay) {
      payload.push({
        chargeType: "topay",
        chargeName: "To-Pay Charges",
        isSelected: true,
      });
    }

    return payload;
  };

  // Save state to localStorage whenever it changes
  useEffect(() => {
    const stateToSave: OtherDetailsState = {
      appointmentBasedDelivery,
      selectedDate: selectedDate ? selectedDate.toISOString() : null,
      multiStoreyWithFloor,
      numberOfFloors,
      pod,
      toPay,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
  }, [
    appointmentBasedDelivery,
    selectedDate,
    multiStoreyWithFloor,
    numberOfFloors,
    pod,
    toPay,
  ]);

  // Update payload whenever relevant states change
  useEffect(() => {
    if (onPayloadChange) {
      const payload = generatePayload();
      onPayloadChange(payload);
    }
  }, [
    appointmentBasedDelivery,
    selectedDate,
    multiStoreyWithFloor,
    numberOfFloors,
    pod,
    toPay,
  ]);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDatePicker]);

  return (
    <div
      className={`
  w-full
  p-4
  items-start
  ${showDatePicker ? "min-h-[400px]" : ""}
  `}
    >
      {/* Appointment Based Delivery */}
      <div className="flex  gap-8 w-full">
        <Checkbox
          label="Appointment Based Delivery"
          checked={appointmentBasedDelivery}
          onChange={(e: any) => setAppointmentBasedDelivery(e.value)}
          className="!text-sm !gap-2 !text-black !font-Open"
          inputElementClass="!h-4 !w-4 !bg-black !border-black !text-black"
        />

        <div className="flex flex-col gap-1 flex-1">
          <div className="relative w-[300px]" ref={datePickerRef}>
            <div
              className={`text-sm w-full flex gap-2 items-center rounded-2xl py-2 px-4 border h-[48px] ${
                appointmentBasedDelivery
                  ? "text-gray-600 border-[#A4A4A4] cursor-pointer bg-white"
                  : "text-gray-400 border-gray-300 cursor-not-allowed bg-gray-50"
              }`}
              onClick={() => {
                if (appointmentBasedDelivery) {
                  setShowDatePicker(!showDatePicker);
                }
              }}
            >
              <IoCalendarOutline size={18} />
              <span className="truncate">
                {formatDateWithTime(selectedDate?.toString() || "")}
              </span>
            </div>

            {showDatePicker && appointmentBasedDelivery && (
              <div className="absolute mt-1 z-50 w-[500px] ">
                <CustomDate onSelect={handleDateChange} />
              </div>
            )}
          </div>
        </div>
      </div>

    
    </div>
  );
};

export default OtherDetails;


  // {/* Multi-Storey With Floor Option */}
  //     <div className="flex flex-col gap-4 w-full">
  //       <Checkbox
  //         label="Multi-Storey With Floor Option"
  //         checked={multiStoreyWithFloor}
  //         onChange={(e: any) => setMultiStoreyWithFloor(e.value)}
  //         className="!text-sm !gap-2 !text-black !font-Open"
  //         inputElementClass="!h-4 !w-4 !bg-black !border-black !text-black"
  //       />

  //       {multiStoreyWithFloor && (
  //         <CustomInputBox
  //           label="Number of Floors"
  //           value={numberOfFloors}
  //           onChange={handleFloorsChange}
  //           inputType="number"
  //           fixedLabel
  //           className="!h-[48px] !rounded-2xl !border-[#A4A4A4] !text-[12px] !font-Open !text-[#777777]"
  //         />
  //       )}
  //     </div>

  //     {/* POD */}
  //     <div className="flex items-start">
  //       <Checkbox
  //         label="POD"
  //         checked={pod}
  //         onChange={(e: any) => setPod(e.value)}
  //         className="!text-sm !gap-2 !text-black !font-Open"
  //         inputElementClass="!h-4 !w-4 !bg-black !border-black !text-black"
  //       />
  //     </div>

  //     {/* To Pay */}
  //     <div className="flex items-start">
  //       <Checkbox
  //         label="To Pay"
  //         checked={toPay}
  //         onChange={(e: any) => setToPay(e.value)}
  //         className="!text-sm !gap-2 !text-black !font-Open"
  //         inputElementClass="!h-4 !w-4 !bg-black !border-black !text-black"
  //       />
  //     </div>